import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingBag, Minus, Plus, Check, Pill, Plus as Cross, Heart } from 'lucide-react';

export const QuantityStepper = ({ value, onChange }) => {
  const [dir, setDir] = useState(1);
  const change = (d) => { setDir(d); onChange(Math.max(1, value + d)); };
  return (
    <div className="inline-flex items-center gap-1 bg-health-mint rounded-full p-1.5">
      <motion.button whileTap={{ scale: 0.85 }} onClick={() => change(-1)} className="w-10 h-10 rounded-full bg-white text-health-ink flex items-center justify-center shadow-sm hover:text-health-emerald" data-testid="quantity-decrease">
        <Minus className="h-4 w-4" />
      </motion.button>
      <div className="relative w-12 h-10 overflow-hidden flex items-center justify-center">
        <AnimatePresence initial={false} custom={dir} mode="popLayout">
          <motion.span key={value} initial={{ y: dir * 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: dir * -24, opacity: 0 }} className="font-heading text-lg font-bold text-health-ink" data-testid="quantity-display">
            {value}
          </motion.span>
        </AnimatePresence>
      </div>
      <motion.button whileTap={{ scale: 0.85 }} onClick={() => change(1)} className="w-10 h-10 rounded-full bg-white text-health-ink flex items-center justify-center shadow-sm hover:text-health-emerald" data-testid="quantity-increase">
        <Plus className="h-4 w-4" />
      </motion.button>
    </div>
  );
};

const PARTICLE_ICONS = [Pill, Cross, Heart];

export const BurstAddButton = ({ onClick, loading, disabled, label = 'Add to cart' }) => {
  const [bursts, setBursts] = useState([]);
  const handle = async () => {
    const id = Date.now();
    setBursts((b) => [...b, id]);
    setTimeout(() => setBursts((b) => b.filter((x) => x !== id)), 900);
    await onClick();
  };
  return (
    <motion.button
      whileHover={disabled ? {} : { scale: 1.02, y: -2 }}
      whileTap={disabled ? {} : { scale: 0.96 }}
      onClick={handle}
      disabled={disabled || loading}
      className="relative w-full h-16 rounded-full bg-health-emerald hover:bg-health-emerald-dark text-white font-heading text-lg font-bold shadow-lift flex items-center justify-center gap-3 overflow-visible disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
      data-testid="add-to-cart-button"
    >
      <span className="absolute inset-0 rounded-full overflow-hidden pointer-events-none">
        <motion.span className="absolute top-0 -left-1/3 w-1/3 h-full bg-gradient-to-r from-transparent via-white/30 to-transparent skew-x-12" animate={{ left: ['-35%', '135%'] }} transition={{ duration: 2.4, repeat: Infinity, repeatDelay: 1.2 }} />
      </span>
      {bursts.map((id) => (
        <span key={id} className="absolute inset-0 pointer-events-none">
          {[...Array(8)].map((_, i) => {
            const Icon = PARTICLE_ICONS[i % 3];
            const angle = (i / 8) * Math.PI * 2;
            return (
              <motion.span key={i} initial={{ x: 0, y: 0, opacity: 1, scale: 0.6 }} animate={{ x: Math.cos(angle) * 90, y: Math.sin(angle) * 50 - 20, opacity: 0, scale: 1.1, rotate: 180 }} transition={{ duration: 0.8, ease: 'easeOut' }} className="absolute left-1/2 top-1/2 -ml-2 -mt-2 text-health-coral">
                <Icon className="h-4 w-4" />
              </motion.span>
            );
          })}
        </span>
      ))}
      {loading ? (
        <>
          <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> Adding...
        </>
      ) : (
        <>
          <motion.span animate={{ rotate: [0, -12, 12, 0] }} transition={{ duration: 0.6, repeat: Infinity, repeatDelay: 3 }}>
            <ShoppingBag className="h-5 w-5" />
          </motion.span>
          {label}
        </>
      )}
    </motion.button>
  );
};

export const SuccessCheck = () => (
  <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 260, damping: 14 }} className="relative w-20 h-20 mx-auto mb-5">
    <motion.span className="absolute inset-0 rounded-full bg-health-mint" animate={{ scale: [1, 1.35, 1], opacity: [1, 0, 1] }} transition={{ duration: 1.6, repeat: Infinity }} />
    <span className="absolute inset-0 rounded-full bg-health-emerald flex items-center justify-center">
      <Check className="h-10 w-10 text-white" strokeWidth={3} />
    </span>
  </motion.div>
);
